/** A *directed* edge key, for the one place direction matters (`boundaryLoopOfFaceSet`'s edge cancellation). Injective for the same reason `edgeId`'s JSON form is; purely internal, never surfaced, so it does not need the readable form. */
function directedEdgeKey(fromVertexId, toVertexId) {
  return JSON.stringify([fromVertexId, toVertexId]);
}

/**
 * The directed boundary edges of a set of faces, via directed-edge
 * cancellation. An edge shared by two faces *within the set* is walked in
 * both directions and cancels out; every survivor retains its source face's
 * winding direction. Unlike `boundaryLoopOfFaceSet`, this deliberately also
 * represents valid regions with more than one boundary loop (for example an
 * inset ring after its cap has been extruded).
 */
export function boundaryEdgesOfFaceSet(faces) {
  const directedEdges = [];
  for (const face of faces) {
    const ids = face.vertexIds;
    for (let i = 0; i < ids.length; i++) directedEdges.push([ids[i], ids[(i + 1) % ids.length]]);
  }
  // `directedEdgeKey`, not a delimiter-joined string: a space-joined pair
  // collides for exactly the same reason an underscore-joined one does (see
  // `edgeId`), and a collision here would cancel a real boundary edge against
  // an unrelated interior one, silently producing the wrong boundary loop.
  const forwardKeys = new Set(directedEdges.map(([a, b]) => directedEdgeKey(a, b)));
  return directedEdges.filter(([a, b]) => !forwardKeys.has(directedEdgeKey(b, a)));
}

/**
 * The directed simple boundary loops of a face set. Unlike
 * `boundaryLoopOfFaceSet`, this accepts more than one closed loop so a
 * read-only consumer can represent a valid annular coplanar region. It still
 * rejects open chains and branches instead of treating malformed/non-manifold
 * face connectivity as geometry adjacency.
 */
export function boundaryLoopsOfFaceSet(faces, commandName) {
  const boundaryEdges = boundaryEdgesOfFaceSet(faces);
  if (boundaryEdges.length === 0) {
    throw new Error(`${commandName} region has no boundary edges (a fully enclosed coplanar patch has no boundary to derive from)`);
  }

  const nextOnBoundary = new Map();
  const incomingCountByVertexId = new Map();
  for (const [from, to] of boundaryEdges) {
    if (nextOnBoundary.has(from)) {
      throw new Error(`${commandName} region boundary is not a collection of simple loops (a vertex starts more than one boundary edge)`);
    }
    nextOnBoundary.set(from, to);
    const incomingCount = (incomingCountByVertexId.get(to) ?? 0) + 1;
    if (incomingCount > 1) {
      throw new Error(`${commandName} region boundary is not a collection of simple loops (more than one boundary edge ends at a vertex)`);
    }
    incomingCountByVertexId.set(to, incomingCount);
  }
  if (nextOnBoundary.size !== boundaryEdges.length || incomingCountByVertexId.size !== boundaryEdges.length) {
    throw new Error(`${commandName} region boundary is not a collection of simple loops (an open chain, not closed loops)`);
  }

  const unvisited = new Set(nextOnBoundary.keys());
  const loops = [];
  while (unvisited.size > 0) {
    const start = [...unvisited].sort()[0];
    const loop = [];
    let cursor = start;
    do {
      if (!unvisited.delete(cursor)) {
        throw new Error(`${commandName} region boundary is not a collection of simple loops (a boundary loop crosses itself)`);
      }
      loop.push(cursor);
      cursor = nextOnBoundary.get(cursor);
      if (cursor === undefined) {
        throw new Error(`${commandName} region boundary is not a collection of simple loops (an open chain, not closed loops)`);
      }
    } while (cursor !== start);
    loops.push(loop);
  }
  return loops;
}

/**
 * The ordered outer boundary loop of a disk-shaped set of faces. Shared by
 * face-set modeling operators, which require one simple boundary loop to
 * build an unambiguous cap or wall.
 */
export function boundaryLoopOfFaceSet(faces, commandName) {
  const boundaryEdges = boundaryEdgesOfFaceSet(faces);
  // A region with no boundary edges at all (every edge cancelled) would be a
  // fully self-enclosed coplanar patch — not reachable through coplanarRegion
  // discovery on an ordinary manifold mesh, but if it ever happened building
  // from it would silently produce disconnected, boundary-less geometry.
  // Fail loudly instead of producing that.
  if (boundaryEdges.length === 0) {
    throw new Error(`${commandName} region has no boundary edges (a fully enclosed coplanar patch has no boundary to build from)`);
  }

  const nextOnBoundary = new Map(boundaryEdges.map(([a, b]) => [a, b]));
  if (nextOnBoundary.size !== boundaryEdges.length) {
    throw new Error(`${commandName} region boundary is not a single simple loop (a vertex starts more than one boundary edge)`);
  }
  const boundaryLoop = [];
  const start = boundaryEdges[0][0];
  let cursor = start;
  do {
    boundaryLoop.push(cursor);
    cursor = nextOnBoundary.get(cursor);
    if (cursor === undefined) {
      throw new Error(`${commandName} region boundary is not a single simple loop (an open chain, not a closed one)`);
    }
  } while (cursor !== start && boundaryLoop.length <= boundaryEdges.length);
  if (boundaryLoop.length !== boundaryEdges.length) {
    throw new Error(`${commandName} region boundary is not a single simple loop (disconnected faces or more than one boundary)`);
  }
  return boundaryLoop;
}
